import { useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { PlusCircledIcon } from '@radix-ui/react-icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { proxy, useSnapshot } from 'valtio'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Drawer } from '@/components/ui/drawer'
import { Form, FormFieldset } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PageCard, PageLayout } from '@/components/ui/page'
import { RadioGroup } from '@/components/ui/radio-group'
import {
	CellText,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toaster/util'

import { invalidateQueries } from '@/libs/helper'
import { useAuth } from '@/libs/hooks/use-auth'

import { paymentCreate, paymentList } from '@/features/payment/api'
import { PAYMENT_TYPES } from '@/features/payment/common'
import type { IPayment } from '@/features/payment/type'

export const Route = createFileRoute('/_app/outlet/payment/')({
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<PageLayout>
			<PageLayout.Header
				breadcrumbs={[
					['/outlet', 'Outlet'],
					['/outlet/payment', 'Payment'],
				]}
			/>
			<PaymentTableCard />
		</PageLayout>
	)
}

const state = proxy({ drawerAddOpen: false })

function PaymentTableCard() {
	return (
		<PageCard>
			<PageCard.Header
				title="Jenis Pembayaran"
				actions={
					<PageCard.HeaderActionRoot>
						<PageCard.HeaderActionButton
							onClick={() => {
								state.drawerAddOpen = true
							}}
						>
							<PlusCircledIcon />
							Tambah Metode
						</PageCard.HeaderActionButton>
					</PageCard.HeaderActionRoot>
				}
			/>
			<DrawerAddPayment />
			<PaymentTable />
		</PageCard>
	)
}

function PaymentTable() {
	const { outletId } = useAuth((s) => s.outlet)
	const { data, isLoading } = useQuery(paymentList.qry({ oId: outletId }))
	const columns = useMemo(getColumnsPayment, [])
	const { table } = useDataTable({
		columns,
		data: data?.payments ?? [],
	})

	return <DataTable table={table} columns={columns} isLoading={isLoading} />
}

const chPayment = createColumnHelper<IPayment>()
function getColumnsPayment() {
	return [
		chPayment.accessor('label', {
			header: 'Label',
			cell: ({ row }) => <CellText text={row.original.label} />,
		}),
		chPayment.accessor('desc', {
			header: 'Description',
			cell: ({ row }) => <CellText text={row.original.desc} />,
		}),
		chPayment.accessor('type', {
			header: 'Type',
			cell: ({ row }) => <CellText text={row.original.type} />,
		}),
	]
}

function DrawerAddPayment() {
	return (
		<Drawer
			open={useSnapshot(state).drawerAddOpen}
			onOpenChange={(v) => (state.drawerAddOpen = v)}
		>
			<Drawer.Content>
				<Drawer.Header>
					<Drawer.Title>Tambah Metode Pembayaran</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body>
					<PaymentForm />
				</Drawer.Body>
			</Drawer.Content>
		</Drawer>
	)
}

type PaymentFormDto = z.infer<typeof PaymentFormDto>
const PaymentFormDto = z.object({
	label: z.string().min(1),
	desc: z.string(),
	type: z.enum(['cash', 'cashless']),
})

function PaymentForm() {
	const form = useForm<PaymentFormDto>({
		resolver: zodResolver(PaymentFormDto),
		defaultValues: getDefaultValues(),
	})

	const { mutateAsync } = useMutation({
		mutationFn: async (data: PaymentFormDto) => {
			await paymentCreate.api({
				body: {
					oId: useAuth.getState().outlet.outletId,
					...data,
				},
			})
		},
		onSuccess: () => {
			state.drawerAddOpen = false
			invalidateQueries([paymentList.key])
		},
	})

	const onSubmit = form.handleSubmit(async (data) => {
		toast.promise(mutateAsync(data), {
			id: 'create',
			loading: 'Menyimpan data...',
			success: 'Data berhasil disimpan',
			error: 'Terjadi kesalahan saat menyimpan data',
		})
	}, console.error)

	return (
		<Form {...form}>
			<div className="space-y-3">
				<Form.Field
					control={form.control}
					name="label"
					render={({ field }) => (
						<FormFieldset.One label="Label">
							<Input {...field} placeholder="Label" />
						</FormFieldset.One>
					)}
				/>
				<Form.Field
					control={form.control}
					name="desc"
					render={({ field }) => (
						<FormFieldset.One label="Deksripsi">
							<Textarea
								{...field}
								placeholder="Masukkan deksripsi"
								className="bg-ui-bg-field"
							/>
						</FormFieldset.One>
					)}
				/>
				<Form.Field
					control={form.control}
					name="type"
					render={({ field }) => (
						<FormFieldset.One label="Jenis Pembayaran">
							<RadioGroup
								value={field.value}
								onValueChange={field.onChange}
								className="relative"
							>
								{PAYMENT_TYPES.map(({ value, label, desc }) => (
									<RadioGroup.ChoiceBox
										key={value}
										value={value}
										label={label}
										description={desc}
									/>
								))}
							</RadioGroup>
						</FormFieldset.One>
					)}
				/>
			</div>
			<div className="mt-4 flex justify-end gap-2">
				<Button
					variant="secondary"
					onClick={() => (state.drawerAddOpen = false)}
				>
					Batal
				</Button>
				<Button onClick={onSubmit}>Simpan</Button>
			</div>
		</Form>
	)
}

function getDefaultValues(init?: PaymentFormDto): PaymentFormDto {
	return (
		init ?? {
			label: '',
			desc: '',
			type: 'cash',
		}
	)
}
